/**
 * What the sign-in screen says when Supabase Auth refuses something.
 *
 * Lives in lib/ rather than in routes/SignIn.tsx so the suite can call it:
 * a route pulls in an SVG the test bundler cannot resolve (Traps).
 *
 * THE STAGE DECIDES WHICH RULES MAY FIRE, AND THAT IS THE 23 SEP FIX. A send
 * that fails comes back as e.g. "Error sending confirmation OTP to provider:
 * Invalid From Number …" — which contains both "invalid" and "otp", so the
 * old single list told somebody who had not yet been sent a code that
 * "That code doesn't match". Code-shaped messages are only possible AFTER a
 * code exists, so they are only checked at the verify stage, and a send
 * failure is checked first at the send stage.
 *
 * THE LANGUAGE CHANGES THE WORDS AND NEVER THE RULES (SHL-25, 24 Sep). The
 * matching below reads Supabase's English message whatever the reader's
 * language, and returns the same KIND of answer from one table per language —
 * so a rule added for English cannot quietly be missing in Spanish. The
 * Spanish is draft and needs a native reader.
 *
 * ── AN UNKNOWN ADDRESS IS TOLD SO (David, 8 Oct — REVERSES ACC-39) ────────
 *
 * 010's trigger (and 101's, for phones) refuses an address with no identity
 * row. From 25 Sep to 8 Oct that refusal went to the same conditional code
 * step as a real send ("If <address> is set up…"), so the public screen never
 * said which addresses are on the roster (B13). On launch day that cost more
 * than it bought: somebody typing the wrong address waited for a code that
 * was never coming, with nothing telling them why. David, 8 Oct: "if the
 * email address or phone number is not found, then say it's not found", and
 * point at support. So the refusal is now an ERROR — `notFoundEmail` /
 * `notFoundPhone`, both naming OneConetec support (SUPPORT_ADDRESS).
 *
 * WHAT THIS GIVES UP, stated: the sign-in screen now answers "is this address
 * on the roster?" for anyone who types one. GoTrue's own /otp rate limit is
 * the only brake on doing that in bulk. (The /otp response always differed —
 * ACC-40 — so the API was never uniform; now the screen is not either.)
 *
 * GoTrue never passes the trigger's words to the browser — it answers HTTP 500
 * "Database error saving new user" (prod auth log, 7 Oct) — so
 * isUninvitedRefusal matches that sentence as well. A genuine database fault
 * on a FIRST sign-in will therefore also read "not found"; the support line
 * in the message is the way out, and the auth log names the real cause.
 *
 * Somebody whose address IS known but whose access is suspended (a leaver,
 * a manual deactivation) is not refused here — their identity exists, so
 * they get a code. App.tsx catches them after sign-in (the `inactive` state).
 */
import type { SignInLang } from './signInCopy'

export type SignInMethod = 'email' | 'phone'
export type SignInStage = 'send' | 'verify'

/**
 * The database's "not on the roster" refusal, in any casing.
 *
 * GOTRUE DOES NOT PASS THE TRIGGER'S WORDS THROUGH (measured 7 Oct, prod auth
 * log). 010 raises "No invitation exists for this address" inside the
 * auth.users insert; GoTrue logs that, and answers the browser with HTTP 500,
 * error_code `unexpected_failure`, message "Database error saving new user" —
 * nothing more. The 25 Sep version matched only the trigger's words, so every
 * uninvited address printed GoTrue's sentence on screen: a raw error that
 * read as the site being broken (Pamela Lupaca, 7 Oct, before 135 reached
 * prod) AND the roster oracle ACC-39 exists to close, since an invited
 * address goes to the code step.
 *
 * The cost, stated: that sentence is GoTrue's answer to ANY failure inserting
 * a new auth user, so a genuine database fault on a FIRST sign-in now also
 * lands on the code step instead of an error. The code step's "no code?" help
 * covers it, and the auth log still names the cause. Existing users never
 * take this path — they are not inserted.
 */
export function isUninvitedRefusal(message: string): boolean {
  const m = message.toLowerCase()
  return m.includes('no invitation exists') || m.includes('database error saving new user')
}

/**
 * What the screen does after "Email me a code" / "Text me a code", from what
 * Supabase answered. THE ONE PLACE THAT DECIDES IT, so the suite can assert
 * what an unknown address is told without mounting the route.
 */
export type SendOutcome = { kind: 'code' } | { kind: 'error'; message: string }

export function sendOutcome(
  errorMessage: string | null | undefined,
  method: SignInMethod,
  lang: SignInLang = 'en',
): SendOutcome {
  if (!errorMessage) return { kind: 'code' }
  const shown = humaniseSignInError(errorMessage, method, 'send', lang)
  return { kind: 'error', message: shown ?? errorMessage }
}

/**
 * The resend cooldown. GoTrue refuses a second code to the SAME address
 * inside 60 s ("For security purposes…"), and only for an address it actually
 * sent to — an uninvited one is refused by 010 first, every time. So a client
 * cooldown SHORTER than GoTrue's lets an invited address show "wait N seconds"
 * where an uninvited one shows nothing, which is ACC-39 again through a side
 * door. It was 45 s. Keep it at or above GoTrue's.
 */
export const RESEND_COOLDOWN_MS = 60_000

type ErrorWords = {
  waitSeconds: (s: string) => string
  waitAMinute: string
  hourlyCap: string
  tooMany: string
  notATextableNumber: string
  couldNotSendEmail: string
  couldNotSendText: string
  expired: string
  doesNotMatch: string
  notFoundEmail: string
  notFoundPhone: string
}

/**
 * Where somebody who cannot get in is sent. The one place. David, 8 Oct
 * (portal@conetec.com); moved to HR's mailbox by David, 11 Oct.
 */
export const SUPPORT_ADDRESS = 'hr@conetec.com'

export const SIGN_IN_ERRORS: Record<SignInLang, ErrorWords> = {
  en: {
    waitSeconds: (s) => `Wait ${s} seconds before asking for another code.`,
    waitAMinute: 'Wait a minute before asking for another code.',
    hourlyCap:
      'Too many codes have been sent from this system in the past hour. This is a limit on our side, not yours — please tell the build team.',
    tooMany: 'Too many attempts. Wait a few minutes and try again.',
    notATextableNumber: 'That does not look like a mobile number we can text. Check the country and the digits.',
    couldNotSendEmail: 'We could not send the email. This is our problem, not yours — please tell the build team.',
    couldNotSendText:
      'We could not send the text. This is our problem, not yours — try your email address, or tell the build team.',
    expired: 'That code has expired. Ask for a new one.',
    doesNotMatch: "That code doesn't match. Check the digits and try again.",
    notFoundEmail:
      `We couldn't find that email address. Check it for typos and try again. If you think this is an error, contact OneConetec support at ${SUPPORT_ADDRESS}.`,
    notFoundPhone:
      `We couldn't find that mobile number. Check the country and the digits, or use your email address instead. If you think this is an error, contact OneConetec support at ${SUPPORT_ADDRESS}.`,
  },
  es: {
    waitSeconds: (s) => `Espera ${s} segundos antes de pedir otro código.`,
    waitAMinute: 'Espera un minuto antes de pedir otro código.',
    hourlyCap:
      'Se enviaron demasiados códigos desde este sistema en la última hora. Es un límite de nuestro lado, no tuyo: avísale al equipo de la plataforma.',
    tooMany: 'Demasiados intentos. Espera unos minutos y vuelve a intentarlo.',
    notATextableNumber:
      'Ese no parece un número de celular al que podamos enviar un SMS. Revisa el país y los dígitos.',
    couldNotSendEmail:
      'No pudimos enviar el correo. El problema es nuestro, no tuyo: avísale al equipo de la plataforma.',
    couldNotSendText:
      'No pudimos enviar el SMS. El problema es nuestro, no tuyo: prueba con tu correo electrónico o avísale al equipo de la plataforma.',
    expired: 'Ese código venció. Pide uno nuevo.',
    doesNotMatch: 'Ese código no coincide. Revisa los dígitos y vuelve a intentarlo.',
    notFoundEmail:
      `No encontramos esa dirección de correo. Revisa que esté bien escrita y vuelve a intentarlo. Si crees que es un error, contacta al soporte de OneConetec en ${SUPPORT_ADDRESS}.`,
    notFoundPhone:
      `No encontramos ese número de celular. Revisa el país y los dígitos, o usa tu correo electrónico. Si crees que es un error, contacta al soporte de OneConetec en ${SUPPORT_ADDRESS}.`,
  },
}

export function humaniseSignInError(
  message: string,
  method: SignInMethod,
  stage: SignInStage,
  lang: SignInLang = 'en',
): string | null {
  const w = SIGN_IN_ERRORS[lang]
  const m = message.toLowerCase()

  // Not on the roster: say so, and where to go (David, 8 Oct). See the header.
  if (isUninvitedRefusal(message)) return method === 'email' ? w.notFoundEmail : w.notFoundPhone

  // Two different limits that both read as "rate limit" and need opposite
  // advice. The per-address cooldown clears in under a minute; the hourly cap
  // does not.
  if (m.includes('for security purposes') || m.includes('only request this after')) {
    const seconds = message.match(/(\d+)\s*seconds?/)?.[1]
    return seconds ? w.waitSeconds(seconds) : w.waitAMinute
  }
  if (m.includes('email rate limit') || m.includes('over_email_send_rate_limit')
      || m.includes('sms rate limit') || m.includes('over_sms_send_rate_limit')) {
    return w.hourlyCap
  }
  if (m.includes('rate limit') || m.includes('too many')) {
    return w.tooMany
  }

  if (stage === 'send') {
    if (method === 'phone' && (m.includes('invalid phone') || m.includes('not a valid phone')
        || (m.includes('phone number') && m.includes('invalid') && !m.includes('from number')))) {
      return w.notATextableNumber
    }
    if (m.includes('error sending') || m.includes('provider') || m.includes('smtp')
        || m.includes('sms_send_failed') || m.includes('unsupported phone')) {
      return method === 'email' ? w.couldNotSendEmail : w.couldNotSendText
    }
    return message
  }

  if (m.includes('expired')) {
    return w.expired
  }
  if (m.includes('invalid') && (m.includes('token') || m.includes('otp') || m.includes('code'))) {
    return w.doesNotMatch
  }
  return message
}
